// Lazy load map iframes when user scrolls near the placeholder image
// This improves SEO by showing a static image to crawlers while lazy-loading
// the interactive iframe for users

export function init() {
  // Check for query parameter to disable lazy loading (for debugging static maps)
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get('static_map') === 'true') {
    return; // Skip lazy loading, keep static images visible
  }

  const lazyMapContainers = document.querySelectorAll('.lazy-map-container');

  if (lazyMapContainers.length === 0) {
    return;
  }

  // Check if IntersectionObserver is supported
  if (!('IntersectionObserver' in window)) {
    // Fallback: load all iframes immediately if IntersectionObserver is not supported
    lazyMapContainers.forEach(loadIframe);
    return;
  }

  // Create IntersectionObserver to detect when placeholder is near viewport
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        loadIframe(entry.target);
        observer.unobserve(entry.target);
      }
    });
  }, {
    // Start loading when placeholder is 200px away from viewport
    rootMargin: '200px'
  });

  // Observe each lazy map container
  lazyMapContainers.forEach((container) => {
    observer.observe(container);
  });

  function loadIframe(container) {
    const placeholder = container.querySelector('.lazy-map-placeholder');
    const stackedImages = container.querySelector('.stacked-map-images');
    const iframeContainer = container.querySelector('.lazy-map-iframe-container');
    const iframe = container.querySelector('.lazy-map-iframe');

    if (!placeholder || !iframeContainer || !iframe) {
      return;
    }

    const iframeSrc = placeholder.dataset.iframeSrc;
    if (!iframeSrc) {
      return;
    }

    // Set iframe src and show it
    iframe.src = iframeSrc;
    iframeContainer.style.display = 'block';

    // Hide stacked images container (map + elevation profile) when iframe loads (but keep in DOM for SEO)
    iframe.addEventListener('load', () => {
      if (stackedImages) {
        stackedImages.style.display = 'none';
      } else {
        // Fallback: hide individual images if container not found
        placeholder.style.display = 'none';
        const elevationProfile = container.querySelector('.lazy-map-elevation-profile');
        if (elevationProfile) {
          elevationProfile.style.display = 'none';
        }
      }
    });
  }
}

// Run as soon as the DOM is ready — and immediately if it already is. This pack
// is appended after the page's other scripts, so it can execute after
// DOMContentLoaded has already fired; a plain listener would then never run,
// and the page would sit on the static placeholder instead of the live map.
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
